/**
 * SiteLinkImport.jsx — "Paste website link" at the top of the Real Estate builder.
 *
 * A buyer's order from the website's order page arrives as a link. Pasting it
 * here fills the quote's size and services on the New price list (the quote
 * moves to New if it isn't), then lists whatever didn't come across exactly.
 * With services already on the quote, a dialog compares the two totals first:
 * the link REPLACES them, never merges (Cyprian, 2026-10-03). Client, address
 * and dates are left alone. Decoding and mapping live in lib/siteOrderLink.
 */
import { useState } from 'react';
import { parseSiteOrderLink, siteOrderToQuote, rgSelectionTotal } from '../../lib/siteOrderLink';
import { RG_PRICE_VERSIONS, RG_CURRENT_VERSION, rgNormalizeVersion } from '../../data/serviceCatalog';
import { fmtC } from '../../lib/utils';

const NOTE_GROUPS = [
  { kind: 'unmatched', title: "Couldn't match", cls: 'text-amber-600' },
  { kind: 'differs', title: 'Priced differently', cls: 'text-text-primary' },
  { kind: 'info', title: 'Notes', cls: 'text-text-muted' },
];

const fmtSqft = (v) => `${Number(v).toLocaleString('en-US')} sq ft`;

export default function SiteLinkImport({ sqft, priceVersion, hasServices, currentReTotal, hasCustomLines, onImport }) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  // { quote, total } waiting on the replace dialog
  const [pending, setPending] = useState(null);
  // What the last import brought in, and its notes
  const [result, setResult] = useState(null);

  const apply = ({ quote, total }) => {
    onImport(quote);
    const known = quote.notes.every((n) => n.amount != null);
    const gap = quote.notes.reduce((s, n) => s + (n.amount || 0), 0);
    setResult({
      notes: quote.notes,
      services: Object.keys(quote.selectedProducts).length,
      addons: Object.keys(quote.selectedAddons).length,
      total,
      // The website's own total, where every difference has a known amount.
      siteTotal: known && gap ? total + gap : null,
    });
    setPending(null);
    setText('');
  };

  const run = (raw) => {
    const order = parseSiteOrderLink(raw);
    if (!order) {
      setResult(null);
      setError("That isn't a website order link. Copy it from the address bar of the order page (rotoflight.com/real-estate/order?...).");
      return;
    }
    const quote = siteOrderToQuote(order, { sqft });
    if (!Object.keys(quote.selectedProducts).length) {
      setError('Nothing in that link could be imported, so the quote is unchanged.');
      setResult({ notes: quote.notes, services: 0, addons: 0, total: 0, siteTotal: null });
      return;
    }
    setError('');
    const next = { quote, total: rgSelectionTotal({ ...quote, sqft: quote.sqft || sqft }) };
    if (hasServices) setPending(next);
    else apply(next);
  };

  const versionMeta = (id) => RG_PRICE_VERSIONS.find((v) => v.id === id) || {};
  const from = versionMeta(rgNormalizeVersion(priceVersion));
  const to = versionMeta(RG_CURRENT_VERSION);
  const movesList = rgNormalizeVersion(priceVersion) !== RG_CURRENT_VERSION;

  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide mb-3">
        Paste website link
      </h3>
      <div className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setError(''); }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (!pasted) return;
            e.preventDefault();
            setText(pasted);
            run(pasted);
          }}
          // preventDefault: the same Enter would otherwise land on the dialog's
          // button as it opens and replace the quote unasked (2026-10-03).
          onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) { e.preventDefault(); run(text); } }}
          placeholder="https://rotoflight.com/real-estate/order?..."
          aria-label="Website order link"
          className="field w-full text-sm"
        />
        <button
          type="button"
          disabled={!text.trim()}
          onClick={() => run(text)}
          className="px-4 py-2 text-sm bg-surface border border-accent/40 text-accent rounded hover:bg-accent/10 transition-colors whitespace-nowrap disabled:opacity-50"
        >
          Import
        </button>
      </div>
      <p className="text-2xs text-text-dim mt-1.5">
        Fills the size and services from an order built on the website, on the New price list.
        Client, address and dates stay as they are.
      </p>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      {result && (
        <div className="mt-3 rounded border border-border bg-bg p-3 space-y-2 text-xs">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-text-primary font-medium">
              {result.services > 0
                ? `Imported ${result.services} service${result.services !== 1 ? 's' : ''}${result.addons ? ` + ${result.addons} add-on${result.addons !== 1 ? 's' : ''}` : ''}: ${fmtC(result.total)}`
                : 'Nothing imported'}
            </span>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="text-2xs text-text-dim hover:text-text-primary transition-colors"
            >
              Dismiss
            </button>
          </div>
          {result.siteTotal != null && (
            <p className="text-text-muted">
              {`The website order comes to ${fmtC(result.siteTotal)}. The difference is listed below.`}
            </p>
          )}
          {result.services > 0 && result.notes.length === 0 && (
            <p className="text-text-muted">Everything matched the website order.</p>
          )}
          {NOTE_GROUPS.map(({ kind, title, cls }) => {
            const notes = result.notes.filter((n) => n.kind === kind);
            if (!notes.length) return null;
            return (
              <div key={kind}>
                <div className="text-2xs text-text-dim uppercase tracking-wider font-medium mb-1">{title}</div>
                <ul className="space-y-1">
                  {notes.map((n, i) => (
                    <li key={i} className={`flex items-start gap-1.5 ${cls}`}>
                      <span className="text-accent">&bull;</span>
                      <span>{n.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Replace confirmation: same pattern as the price-list switch ── */}
      {pending && (() => {
        const delta = pending.total - currentReTotal;
        const newSqft = pending.quote.sqft;
        const sizeMoves = newSqft && String(parseInt(sqft, 10) || '') !== newSqft;
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Replace the services?"
            onKeyDown={(e) => { if (e.key === 'Escape') setPending(null); }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setPending(null); }}
            className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50"
          >
            <div className="bg-surface border border-border rounded-xl shadow-xl w-80 p-5 space-y-4">
              <h3 className="text-sm font-heading text-text-primary uppercase">Replace the services?</h3>
              <p className="text-xs text-text-muted">
                {"The link's services replace every Real Estate service on this quote"}
                {movesList ? `, and the quote moves to the ${to.label} price list (${to.sub})` : ''}
                {'. Rates typed by hand go back to the list price. Client, address and dates stay'}
                {hasCustomLines ? ', and so do your custom lines.' : '.'}
              </p>
              <div className="rounded border border-border bg-bg p-3 space-y-1 text-xs">
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Now{movesList ? `, ${from.label} (${from.sub})` : ''}</span>
                  <span className="text-text-primary">{fmtC(currentReTotal)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">From the link{movesList ? `, ${to.label} (${to.sub})` : ''}</span>
                  <span className="text-text-primary font-semibold">{fmtC(pending.total)}</span>
                </div>
                <div className="flex justify-between gap-3 border-t border-border pt-1">
                  <span className="text-text-muted">Difference</span>
                  <span className="text-text-primary font-semibold">
                    {!delta ? 'None, same total' : `${delta > 0 ? '+' : '−'}${fmtC(Math.abs(delta))}`}
                  </span>
                </div>
              </div>
              {sizeMoves && (
                <p className="text-2xs text-text-dim">
                  {`Size: ${parseInt(sqft, 10) > 0 ? fmtSqft(parseInt(sqft, 10)) : 'none'} becomes ${fmtSqft(newSqft)}.`}
                </p>
              )}
              <div className="flex justify-end gap-2">
                {/* Focus starts on Cancel: replacing a quote is the step that
                    should take a deliberate click (2026-10-03). */}
                <button
                  autoFocus
                  onClick={() => setPending(null)}
                  className="px-4 py-1.5 text-sm text-text-muted hover:text-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => apply(pending)}
                  className="px-4 py-1.5 text-sm text-white rounded bg-accent hover:opacity-90"
                >
                  Replace
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
